import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["switch", "label"]
  static values = { 
    accountId: String,
    accountName: String,
    currentTotpEnabled: Boolean
  }

  connect() {
    this.setupConfirmModal()
  }

  setupConfirmModal() {
    // Create modal HTML dynamically
    const modalHtml = `
      <div class="modal fade" id="totpToggleModal" tabindex="-1" aria-labelledby="totpToggleModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-body">
              <h5 class="fw-bold" id="totpToggleModalLabel"></h5>
              <p>Are you sure you want to continue?</p>
              <p class="text-danger fw-bold" id="confirmModalMessage"></p>
              <p class="text-danger fw-bold" id="orgName"></p>
            </div>
            <div class="modal-footer border-0">
              <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-primary" id="confirmToggleBtn">Confirm</button>
            </div>
          </div>
        </div>
      </div>
    `
    
    // Append modal to body
    document.body.insertAdjacentHTML('beforeend', modalHtml)
    
    // Initialize modal
    this.confirmModal = new bootstrap.Modal(document.getElementById('totpToggleModal'))
    
    // Add event listener for confirm button
    document.getElementById('confirmToggleBtn').addEventListener('click', () => {
      this.confirmToggle()
    })
  }

  toggle(event) {
    event.preventDefault()
    
    const isChecked = this.switchTarget.checked
    const modalContent = this.getModalContent(isChecked)
    
    this.updateModalContent(modalContent)
    this.confirmModal.show()
  }

  getModalContent(isChecked) {
    return {
      title: isChecked ? 'Set up the Authenticator App for this organization' : 'Remove the Authenticator App from this organization',
      message: isChecked ? 
        'This will enable 2-step verification for this organization.' : 
        'This will disable 2-step verification for this organization.',
      buttonText: isChecked ? 'Set Up' : 'Remove'
    }
  }

  updateModalContent(content) {
    document.getElementById('totpToggleModalLabel').textContent = content.title
    document.getElementById('confirmModalMessage').textContent = content.message
    document.getElementById('orgName').textContent = this.accountNameValue
    document.getElementById('confirmToggleBtn').textContent = content.buttonText
  }

  confirmToggle() {
    const isChecked = this.switchTarget.checked
    const formData = this.prepareFormData(isChecked)
    
    this.sendToggleRequest(formData, isChecked)
  }

  prepareFormData(isChecked) {
    const formData = new FormData()
    formData.append('account[enable_totp]', isChecked)
    return formData
  }

  sendToggleRequest(formData, isChecked) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    
    fetch(`/accounts/${this.accountIdValue}/toggle_2fa`, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.handleToggleResponse(data, isChecked)
      } else if (data.require_totp) {
        // Show TOTP modal
        this.confirmModal.hide()
        this.showTotpModal(isChecked)
      } else {
        this.handleToggleError(data, isChecked)
      }
    })
    .catch(error => this.handleToggleError(error, isChecked))
    .finally(() => this.confirmModal.hide())
  }

  showTotpModal(isChecked) {
    // Use the reusable TOTP verification modal
    const event = new CustomEvent('showTotpModal', {
      detail: {
        actionUrl: `/accounts/${this.accountIdValue}/toggle_2fa`,
        actionMethod: 'POST',
        formData: {
          account: {
            enable_totp: isChecked
          }
        }
      }
    })
    document.dispatchEvent(event)
  }

  handleToggleResponse(data, isChecked) {
    if (data.success) {
      this.updateUI(isChecked)
      
      // Redirect if redirect_url is provided
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      }
    } else {
      this.revertSwitchState(isChecked)
      alert(data.error || 'Failed to update TOTP settings.')
    }
  }

  handleToggleError(error, isChecked) {
    console.error('Error:', error)
    this.revertSwitchState(isChecked)
    alert('Failed to update TOTP settings.')
  }

  updateUI(isChecked) {
    this.labelTarget.textContent = isChecked ? 'On' : 'Off'
    this.currentTotpEnabledValue = isChecked
  }

  revertSwitchState(isChecked) {
    this.switchTarget.checked = !isChecked
  }

  disconnect() {
    if (this.confirmModal) {
      this.confirmModal.dispose()
    }
  }
} 